/**
 * One copilot drawer, one socket, for the whole recruiter app.
 *
 * Every entry point (the job page header, an applicant row, the Results
 * header, the floating launcher) asks this provider to open, rather than owning a
 * drawer of its own. Separate drawers would stack, and separate sockets would
 * supersede each other with 4409.
 *
 * Mounted around the recruiter route set, outside its Suspense boundary, so
 * the drawer survives navigation and lazy route loads.
 */

import React, {
  Suspense,
  createContext,
  lazy,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ReactReduxContext } from "react-redux";

import { handleInvalidToken } from "../utils/tokenUtils";
import { listCopilotSessions } from "./api";
import { NEW_SESSION, RESUME_WINDOW_MS } from "./constants";
import {
  markNewChat,
  newestSession,
  readLastSession,
  writeLastSession,
} from "./lastSession";
import { latestSessionFor, subjectFromSession, subjectKeyOf } from "./subjects";
import "./copilot.css";
// The floating blob: shows itself only where no sidebar Hairo card is visible.
import CopilotLauncher from "./components/CopilotLauncher";

// The MUI drawer shell (and, inside it, the lazy panel) is its own chunk so
// @mui/material stays out of the entry bundle. It starts loading when this
// provider mounts, so it is normally ready before the first open.
const CopilotDrawer = lazy(() => import("./CopilotDrawer"));

/**
 * Holds only stable callbacks, so the value never changes and no consumer
 * re-renders because the drawer opened. That matters: the job page is a large
 * table, and it consumes this for its row action.
 */
const CopilotContext = createContext(null);

/** null outside a provider — consumers render nothing rather than throw. */
export const useCopilot = () => useContext(CopilotContext);

/**
 * Whether the drawer is open, kept apart from CopilotContext so only the few
 * consumers that need it (the floating launcher hides itself while the panel
 * is open) re-render when it changes. false outside a provider.
 */
const CopilotOpenContext = createContext(false);
export const useCopilotOpen = () => useContext(CopilotOpenContext);

/**
 * A show-your-work task's progress for the entry points while the panel is
 * closed: { id, live, ended, done, total, needsConfirm, title }, or null.
 * Its own context so only the launcher and the sidebar card re-render on it.
 */
const CopilotTaskContext = createContext(null);
export const useCopilotTaskProgress = () => useContext(CopilotTaskContext);

export function CopilotProvider({ children }) {
  const [open, setOpen] = useState(false);
  /** Drawer width: the reference's expand control. Survives subject switches. */
  const [expanded, setExpanded] = useState(false);
  /**
   * Show your work: a task is on screen, so the drawer docks beside the page
   * (no backdrop) and the page makes room for it. Set by the panel.
   */
  const [taskMode, setTaskMode] = useState(false);
  /**
   * The panel's task, reported while it is on screen. A live task keeps the
   * panel (and its socket) mounted when the drawer closes, so the task keeps
   * running in the background; so does an ended one the user has not seen.
   */
  const [taskProgress, setTaskProgress] = useState(null);
  const taskProgressRef = useRef(null);
  const [seenEndId, setSeenEndId] = useState(null);
  /**
   * What the panel connects to. `generation` changes on every explicit
   * switch so the panel remounts — that resets its local UI and makes "New"
   * start a fresh session even when the id is already "new".
   */
  const [target, setTarget] = useState({
    subject: null,
    sessionId: NEW_SESSION,
    generation: 0,
    /** Looking up the subject's latest chat; the panel waits for it. */
    resolving: false,
  });
  /**
   * The subject of the page the user is on, for the global launcher. A ref,
   * not state: it is only read at click time, and state would re-render every
   * consumer on each navigation.
   */
  const pageSubjectRef = useRef(null);
  /** The open panel listens here, so the same chat follows the user's page. */
  const pageSubjectListenersRef = useRef(new Set());
  /** Same idea for pages with no record behind them (Team Performance + period). */
  const pageContextRef = useRef(null);
  /** Rows ticked in the table on screen, as [{ kind, id }], kept live. */
  const selectionRef = useRef(null);

  const openRef = useRef(false);
  /**
   * The live session (its id and its own subject), so reopening continues it.
   * Mirrored to localStorage per user, so a refresh continues it too.
   */
  const liveSessionRef = useRef({ key: null, id: null, subject: null });

  // Read from the store at call time rather than with useSelector: the
  // provider must also work (as it does in tests) without a Redux provider.
  // The store object is stable, so these callbacks (and the context value)
  // are still computed once.
  const store = useContext(ReactReduxContext)?.store ?? null;
  const userId = useCallback(() => {
    try {
      const user = store?.getState?.()?.auth?.user;
      return user?.id ?? user?.user_id ?? null;
    } catch {
      return null;
    }
  }, [store]);

  /**
   * The user's most recent conversation: the one live in this tab, else the
   * one stored for this user. null when there is neither.
   */
  const recentSession = useCallback(() => {
    const live = liveSessionRef.current;
    if (live.id) return { id: live.id, subject: live.subject };
    return readLastSession(userId());
  }, [userId]);

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  useEffect(() => {
    taskProgressRef.current = taskProgress;
  }, [taskProgress]);
  // An ended task counts as seen once the panel is open on it (set while
  // rendering, React's pattern for state derived from a previous render).
  if (open && taskProgress?.ended && seenEndId !== taskProgress.id) {
    setSeenEndId(taskProgress.id);
  }
  const keepAlive = Boolean(
    taskProgress && (taskProgress.live || seenEndId !== taskProgress.id)
  );

  /**
   * Opening never starts a blank chat while the user has one: it continues
   * their most recent conversation until they click New chat.
   *   - On a subject: that subject's chat if it is live in this tab; else the
   *     server's latest chat about it (within RESUME_WINDOW_MS); else the
   *     most recent conversation.
   *   - Subject-less (any other page): the most recent conversation, from
   *     this tab or localStorage; with neither, the server's newest session.
   * A resumed chat always takes the page's subject (none on a page without
   * one): the socket asks the server to follow the page (follow_page).
   * After New chat the "most recent" is that blank chat until it gets an id.
   */
  const openCopilot = useCallback(
    (subject = null) => {
      const key = subjectKeyOf(subject);
      // A task running in the background: reopen exactly that panel.
      if (taskProgressRef.current) {
        setOpen(true);
        return;
      }
      setTarget((current) => {
        // Already showing this subject (or any chat, for a subject-less open):
        // just bring the drawer forward.
        if (
          openRef.current &&
          (key === "none" || subjectKeyOf(current.subject) === key)
        ) {
          return current;
        }
        const base = {
          pageContext: pageContextRef.current,
          generation: current.generation + 1,
        };
        const live = liveSessionRef.current;
        if (key !== "none" && live.id && live.id !== NEW_SESSION && live.key === key) {
          return { ...base, subject, sessionId: live.id, resolving: false };
        }
        if (key === "none") {
          const recent = recentSession();
          if (recent) {
            return { ...base, subject: null, sessionId: recent.id, resolving: false };
          }
        }
        // Nothing in hand: ask the server before connecting.
        return { ...base, subject, sessionId: NEW_SESSION, resolving: true };
      });
      setOpen(true);
    },
    [recentSession]
  );

  // The lookup behind `resolving`. Keyed on generation, so a result for a
  // subject the user has already left is dropped. A failed lookup falls back
  // to the most recent conversation, else starts fresh: the lookup must never
  // stand between the user and a chat.
  useEffect(() => {
    if (!target.resolving) return undefined;
    const { subject, generation } = target;
    const key = subjectKeyOf(subject);
    let cancelled = false;
    listCopilotSessions(25)
      .then((data) => {
        const sessions = data?.sessions;
        if (key !== "none") {
          const own = latestSessionFor(sessions, subject, {
            windowMs: RESUME_WINDOW_MS,
          });
          if (own) return { id: own, subject };
        }
        // A New chat marker means "blank", never the server's newest.
        const recent = recentSession();
        if (recent) return recent.id === NEW_SESSION ? null : recent;
        return newestSession(sessions);
      })
      .catch(() => {
        const recent = recentSession();
        return recent && recent.id !== NEW_SESSION ? recent : null;
      })
      .then((found) => {
        if (cancelled) return;
        setTarget((current) =>
          current.generation === generation
            ? {
                ...current,
                // The page's subject, also for an older chat: it follows the page.
                subject: current.subject,
                sessionId: found?.id ?? NEW_SESSION,
                resolving: false,
              }
            : current
        );
      });
    return () => {
      cancelled = true;
    };
    // `target` changes identity on resolve; generation is what identifies it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.resolving, target.generation]);

  const closeCopilot = useCallback(() => setOpen(false), []);

  // The page shifts left of the docked panel (copilot.css). A modal drawer
  // that opened before the task hid the page from screen readers and locked
  // its scroll; while docked, the page is a page again.
  const docked = open && taskMode;
  useEffect(() => {
    const body = typeof document !== "undefined" ? document.body : null;
    if (!body) return undefined;
    body.classList.toggle("hairo-task-docked", docked);
    const root = document.getElementById("root");
    if (docked && root?.getAttribute("aria-hidden") === "true") {
      root.removeAttribute("aria-hidden");
      return () => {
        body.classList.remove("hairo-task-docked");
        // Still a modal drawer after the task: hide the page again, as MUI had.
        if (openRef.current) root.setAttribute("aria-hidden", "true");
      };
    }
    return () => body.classList.remove("hairo-task-docked");
  }, [docked]);

  const toggleExpanded = useCallback(() => setExpanded((value) => !value), []);

  /**
   * A genuine credential failure (4401, or `revoked` before it). The app's
   * handler opens an antd "Session expired" modal at z-index 1000, which the
   * drawer would cover — so the drawer closes first.
   */
  const handleUnauthorized = useCallback(() => {
    setOpen(false);
    handleInvalidToken();
  }, []);

  /** Resume with the session's OWN subject, never the page's. */
  const resumeSession = useCallback((session) => {
    if (!session?.id) return;
    setTarget((current) => ({
      subject: subjectFromSession(session),
      pageContext: pageContextRef.current,
      sessionId: String(session.id),
      generation: current.generation + 1,
    }));
  }, []);

  // An explicit New chat: from now on, reopening shows this blank chat (and,
  // once it has an id, continues it) instead of the previous conversation.
  const newSession = useCallback(() => {
    // The blank chat is now the "most recent" one, so neither memory nor the
    // server's list brings the previous conversation back.
    liveSessionRef.current = { key: null, id: NEW_SESSION, subject: null };
    markNewChat(userId());
    setTarget((current) => ({
      subject: current.subject,
      pageContext: pageContextRef.current,
      sessionId: NEW_SESSION,
      generation: current.generation + 1,
    }));
  }, [userId]);

  const notifyPageSubject = useCallback(() => {
    pageSubjectListenersRef.current.forEach((listener) => {
      try {
        listener(pageSubjectRef.current);
      } catch {
        // A listener's failure must not break navigation.
      }
    });
  }, []);

  /** Last mount wins; cleanup clears only if it is still the registered one. */
  const registerPageSubject = useCallback((subject) => {
    const key = subjectKeyOf(subject);
    pageSubjectRef.current = subject;
    notifyPageSubject();
    return () => {
      if (subjectKeyOf(pageSubjectRef.current) === key) {
        pageSubjectRef.current = null;
        notifyPageSubject();
      }
    };
  }, [notifyPageSubject]);

  const getPageSubject = useCallback(() => pageSubjectRef.current, []);

  /** `listener(subject)` on every page subject change; returns the unsubscribe. */
  const subscribePageSubject = useCallback((listener) => {
    pageSubjectListenersRef.current.add(listener);
    return () => pageSubjectListenersRef.current.delete(listener);
  }, []);

  /** Last mount wins; cleanup clears only if it is still the registered one. */
  const registerPageContext = useCallback((context) => {
    pageContextRef.current = context;
    return () => {
      if (pageContextRef.current === context) pageContextRef.current = null;
    };
  }, []);

  /** Last mount wins; cleanup clears only if it is still the registered one. */
  const registerSelection = useCallback((selection) => {
    selectionRef.current = selection;
    return () => {
      if (selectionRef.current === selection) selectionRef.current = null;
    };
  }, []);

  /**
   * What the user is looking at, read when a message is SENT (not when Hairo
   * opened): the route and its query string, the page's registered context
   * and the ticked rows. A hint only - the server checks every id it names.
   */
  const getPageView = useCallback(
    () => ({
      path: window.location.pathname,
      query: window.location.search,
      ...(pageContextRef.current || {}),
      selected: selectionRef.current || [],
    }),
    []
  );

  const rememberSession = useCallback(
    (subject, sessionId) => {
      if (!sessionId || sessionId === NEW_SESSION) return;
      liveSessionRef.current = {
        key: subjectKeyOf(subject),
        id: String(sessionId),
        subject: subject ?? null,
      };
      writeLastSession(userId(), sessionId, subject);
    },
    [userId]
  );

  /**
   * Unsent composer text per subject. Closing the drawer unmounts the panel
   * and its composer; this keeps a half-typed question for the reopen. Memory
   * only: a refresh, or leaving the recruiter pages, clears it.
   */
  const draftsRef = useRef(new Map());
  const getDraft = useCallback(
    (subject) => draftsRef.current.get(subjectKeyOf(subject)) ?? "",
    []
  );
  const saveDraft = useCallback((subject, text) => {
    const key = subjectKeyOf(subject);
    if (text) draftsRef.current.set(key, text);
    else draftsRef.current.delete(key);
  }, []);

  // Every member is a stable callback, so this is computed once.
  const value = useMemo(
    () => ({
      openCopilot,
      closeCopilot,
      resumeSession,
      newSession,
      registerPageSubject,
      getPageSubject,
      subscribePageSubject,
      registerPageContext,
      registerSelection,
      getPageView,
    }),
    [
      openCopilot,
      closeCopilot,
      resumeSession,
      newSession,
      registerPageSubject,
      getPageSubject,
      subscribePageSubject,
      registerPageContext,
      registerSelection,
      getPageView,
    ]
  );

  return (
    <CopilotContext.Provider value={value}>
      <CopilotOpenContext.Provider value={open}>
        <CopilotTaskContext.Provider value={open ? null : taskProgress}>
          {children}
          {/* Needs the redux store (role gating); the app always has one. */}
          {store ? <CopilotLauncher /> : null}
        </CopilotTaskContext.Provider>
      </CopilotOpenContext.Provider>
      <Suspense fallback={null}>
        <CopilotDrawer
          open={open}
          expanded={expanded}
          target={target}
          onClose={closeCopilot}
          onToggleExpand={toggleExpanded}
          onUnauthorized={handleUnauthorized}
          onResume={resumeSession}
          onNew={newSession}
          onSessionReady={rememberSession}
          getDraft={getDraft}
          onDraftChange={saveDraft}
          taskMode={taskMode}
          onTaskModeChange={setTaskMode}
          keepAlive={keepAlive}
          onTaskProgress={setTaskProgress}
        />
      </Suspense>
    </CopilotContext.Provider>
  );
}

export default CopilotProvider;
